import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Store, UtensilsCrossed, Coffee, Beer, ShoppingBag,
  Warehouse, Building2, ChevronRight, ChevronLeft,
  Users, Loader2, Check, Palette, Globe, Copy,
  DollarSign, UserPlus, Package, Upload, ImageIcon,
  Plus, Trash2, Info, FileSpreadsheet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

type BusinessType = 'restaurant' | 'qsr' | 'bar' | 'cafe' | 'retail' | 'wholesale' | 'other';

interface BusinessOption {
  value: BusinessType;
  label: string;
  description: string;
  icon: React.ElementType;
}

const BUSINESS_OPTIONS: BusinessOption[] = [
  { value: 'restaurant', label: 'Restaurant', description: 'Full-service dining with kitchen workflow', icon: UtensilsCrossed },
  { value: 'qsr', label: 'Quick Service', description: 'Fast food, counter service, food trucks', icon: Store },
  { value: 'cafe', label: 'Cafe / Bakery', description: 'Coffee shops, bakeries, juice bars', icon: Coffee },
  { value: 'bar', label: 'Bar / Lounge', description: 'Bars, pubs, nightclubs, lounges', icon: Beer },
  { value: 'retail', label: 'Retail Store', description: 'Clothing, electronics, general merchandise', icon: ShoppingBag },
  { value: 'wholesale', label: 'Wholesale', description: 'Bulk distribution, B2B sales', icon: Warehouse },
  { value: 'other', label: 'Other', description: 'Custom setup for any business', icon: Building2 },
];

const CURRENCY_OPTIONS = [
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: 'US' },
  { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$', flag: 'JM' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', flag: 'CA' },
  { code: 'TTD', name: 'Trinidad & Tobago Dollar', symbol: 'TT$', flag: 'TT' },
  { code: 'BBD', name: 'Barbadian Dollar', symbol: 'Bds$', flag: 'BB' },
  { code: 'KYD', name: 'Cayman Islands Dollar', symbol: 'CI$', flag: 'KY' },
  { code: 'BSD', name: 'Bahamian Dollar', symbol: 'B$', flag: 'BS' },
  { code: 'XCD', name: 'Eastern Caribbean Dollar', symbol: 'EC$', flag: 'LC' },
  { code: 'BZD', name: 'Belize Dollar', symbol: 'BZ$', flag: 'BZ' },
  { code: 'GYD', name: 'Guyanese Dollar', symbol: 'G$', flag: 'GY' },
  { code: 'HTG', name: 'Haitian Gourde', symbol: 'G', flag: 'HT' },
  { code: 'AWG', name: 'Aruban Florin', symbol: 'Afl.', flag: 'AW' },
  { code: 'GBP', name: 'British Pound', symbol: '\u00A3', flag: 'GB' },
  { code: 'EUR', name: 'Euro', symbol: '\u20AC', flag: 'EU' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', flag: 'MX' },
];

const THEME_OPTIONS = [
  { value: '#f97316', label: 'Blaze Orange', color: 'bg-orange-500' },
  { value: '#3b82f6', label: 'Blue', color: 'bg-blue-500' },
  { value: '#8b5cf6', label: 'Purple', color: 'bg-violet-500' },
  { value: '#10b981', label: 'Green', color: 'bg-emerald-500' },
  { value: '#ef4444', label: 'Red', color: 'bg-red-500' },
  { value: '#ec4899', label: 'Pink', color: 'bg-pink-500' },
  { value: '#06b6d4', label: 'Cyan', color: 'bg-cyan-500' },
  { value: '#f59e0b', label: 'Amber', color: 'bg-amber-500' },
];

interface CashierEntry {
  firstName: string;
  lastName: string;
  pin: string;
}

interface ProductEntry {
  name: string;
  price: string;
  category: string;
}

// Step indices
const STEP_CURRENCY = 0;
const STEP_BUSINESS = 1;
const STEP_CASHIERS = 2;
const STEP_PRODUCTS = 3;
const STEP_STAFF = 4;
const STEP_LOGO = 5;
const STEP_THEME = 6;
const STEP_FINISH = 7;
const TOTAL_STEPS = 8;

function isFoodBusiness(bt: BusinessType | null): boolean {
  return bt === 'restaurant' || bt === 'qsr' || bt === 'cafe' || bt === 'bar';
}

function catalogLabel(bt: BusinessType | null): string {
  return isFoodBusiness(bt) ? 'menu items' : 'products';
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
  }),
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { updateOrganization, profile, organization } = useAuth();

  // Redirect to dashboard if onboarding already completed
  const onboardingCompleted = (organization as any)?.settings?.onboarding_completed;
  if (onboardingCompleted) {
    navigate('/dashboard', { replace: true });
    return null;
  }
  const { setAccentColor } = useTheme();
  const [step, setStep] = useState(STEP_CURRENCY);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [currency, setCurrency] = useState('USD');
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [cashiers, setCashiers] = useState<CashierEntry[]>([{ firstName: '', lastName: '', pin: '' }]);
  const [products, setProducts] = useState<ProductEntry[]>([{ name: '', price: '', category: '' }]);
  const [staffEmails, setStaffEmails] = useState<string[]>(['']);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [accentColor, setSelectedAccent] = useState<string>('#f97316');
  const [_uploadingLogo, setUploadingLogo] = useState(false);
  const [_creatingCashiers, setCreatingCashiers] = useState(false);
  const [_creatingProducts, setCreatingProducts] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const goNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setDirection(1);
      setStep(s => s + 1);
    }
  };

  const goBack = () => {
    if (step > 0) {
      setDirection(-1);
      setStep(s => s - 1);
    }
  };

  const canAdvance = () => {
    switch (step) {
      case STEP_CURRENCY: return currency !== '';
      case STEP_BUSINESS: return businessType !== null;
      case STEP_CASHIERS: return true; // optional
      case STEP_PRODUCTS: return true; // optional
      case STEP_STAFF: return true; // optional
      case STEP_LOGO: return true; // optional
      case STEP_THEME: return true;
      case STEP_FINISH: return true;
      default: return false;
    }
  };

  // Cashier management
  const addCashier = () => setCashiers(prev => [...prev, { firstName: '', lastName: '', pin: '' }]);
  const removeCashier = (idx: number) => setCashiers(prev => prev.filter((_, i) => i !== idx));
  const updateCashier = (idx: number, field: keyof CashierEntry, value: string) => {
    setCashiers(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  // Product management
  const addProduct = () => setProducts(prev => [...prev, { name: '', price: '', category: '' }]);
  const removeProduct = (idx: number) => setProducts(prev => prev.filter((_, i) => i !== idx));
  const updateProduct = (idx: number, field: keyof ProductEntry, value: string) => {
    setProducts(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  // Staff management
  const addStaffEmail = () => setStaffEmails(prev => [...prev, '']);
  const removeStaffEmail = (idx: number) => setStaffEmails(prev => prev.filter((_, i) => i !== idx));

  // Logo upload handler
  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  // Create cashiers via edge function
  const createCashiers = async () => {
    const validCashiers = cashiers.filter(c => c.firstName.trim() && c.pin.trim() && c.pin.length === 4);
    if (validCashiers.length === 0) return;

    setCreatingCashiers(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      for (const cashier of validCashiers) {
        await supabase.functions.invoke('create-staff', {
          body: {
            first_name: cashier.firstName.trim(),
            last_name: cashier.lastName.trim(),
            pin: cashier.pin,
            role: 'cashier',
          },
        });
      }
      toast.success(`Created ${validCashiers.length} cashier account${validCashiers.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Failed to create cashiers:', err);
      toast.error('Some cashiers could not be created');
    } finally {
      setCreatingCashiers(false);
    }
  };

  // Create products
  const createProducts = async () => {
    const validProducts = products.filter(p => p.name.trim() && p.price.trim());
    if (validProducts.length === 0) return;

    setCreatingProducts(true);
    try {
      const locationId = (organization as any)?.default_location_id;
      // Get first location if no default
      let locId = locationId;
      if (!locId) {
        const { data: locs } = await supabase
          .from('locations')
          .select('id')
          .eq('organization_id', organization?.id as string)
          .limit(1);
        locId = locs?.[0]?.id;
      }

      for (const product of validProducts) {
        // If a category name is provided, find or create the category
        let categoryId = null;
        if (product.category.trim()) {
          const { data: existingCat } = await supabase
            .from('categories')
            .select('id')
            .eq('location_id', locId)
            .eq('name', product.category.trim())
            .single();

          if (existingCat) {
            categoryId = existingCat.id;
          } else {
            const { data: newCat } = await supabase
              .from('categories')
              .insert({ name: product.category.trim(), location_id: locId } as any)
              .select('id')
              .single();
            categoryId = newCat?.id || null;
          }
        }

        await supabase.from('products').insert({
          name: product.name.trim(),
          price: parseFloat(product.price) || 0,
          category_id: categoryId,
          location_id: locId,
          is_active: true,
        } as any);
      }
      toast.success(`Added ${validProducts.length} ${catalogLabel(businessType)}`);
    } catch (err) {
      console.error('Failed to create products:', err);
      toast.error('Some items could not be created');
    } finally {
      setCreatingProducts(false);
    }
  };

  // Upload logo
  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile || !organization?.id) return null;

    setUploadingLogo(true);
    try {
      const ext = logoFile.name.split('.').pop() || 'png';
      const path = `${organization.id}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(path, logoFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('logos').getPublicUrl(path);
      return urlData?.publicUrl || null;
    } catch (err) {
      console.error('Failed to upload logo:', err);
      toast.error('Logo upload failed');
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const headers = ['name', 'price', 'category', 'description', 'sku', 'barcode'];
    const examples = isFoodBusiness(businessType)
      ? [
          ['Espresso', '4.50', 'Coffee', 'Rich single shot espresso', 'ESP-001', ''],
          ['Cappuccino', '5.50', 'Coffee', 'Classic Italian cappuccino', 'CAP-001', ''],
          ['Chicken Sandwich', '12.00', 'Mains', 'Grilled chicken with lettuce and tomato', 'CS-001', ''],
        ]
      : [
          ['T-Shirt (M)', '25.00', 'Clothing', 'Medium cotton t-shirt', 'TS-M-001', '123456789'],
          ['Phone Case', '15.00', 'Accessories', 'Protective phone case', 'PC-001', '987654321'],
          ['Water Bottle', '8.00', 'Drinks', 'Bottled water 500ml', 'WB-001', ''],
        ];

    const csv = [
      headers.join(','),
      ...examples.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blazepos-${isFoodBusiness(businessType) ? 'menu' : 'products'}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleFinish = async () => {
    if (!businessType) return;
    setIsSubmitting(true);

    try {
      // Upload logo if selected
      let logoUrl = organization?.logo_url || null;
      if (logoFile) {
        const uploaded = await uploadLogo();
        if (uploaded) logoUrl = uploaded;
      }

      // Create cashiers if any valid ones
      const validCashiers = cashiers.filter(c => c.firstName.trim() && c.pin.trim() && c.pin.length === 4);
      if (validCashiers.length > 0) {
        await createCashiers();
      }

      // Create products if any valid ones
      const validProducts = products.filter(p => p.name.trim() && p.price.trim());
      if (validProducts.length > 0) {
        await createProducts();
      }

      // Save organization settings
      await updateOrganization({
        business_type: businessType,
        currency: currency,
        logo_url: logoUrl,
        settings: {
          onboarding_completed: true,
          appearance: {
            accentColor: accentColor,
          },
          onboarding_data: {
            completed_at: new Date().toISOString(),
          },
        },
      } as any);

      // Apply the accent color
      setAccentColor(accentColor);

      navigate('/dashboard?onboarded=true');
    } catch (err) {
      console.error('Failed to save onboarding data:', err);
      navigate('/dashboard');
    } finally {
      setIsSubmitting(false);
    }
  };

  const firstName = profile?.first_name || '';

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="bg-zinc-950 text-white px-6 py-3 flex items-center gap-2 border-b border-zinc-900">
        <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
        <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
      </header>

      {/* Progress bar */}
      <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1">
        <motion.div
          className="h-full bg-gradient-to-r from-orange-500 to-orange-600"
          initial={{ width: '0%' }}
          animate={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait" custom={direction}>

            {/* STEP 0: Currency Selection */}
            {step === STEP_CURRENCY && (
              <motion.div key="currency" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-8">
                  <motion.h1
                    className="text-3xl font-bold text-zinc-900 dark:text-white mb-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    {firstName ? `Welcome, ${firstName}!` : 'Welcome!'}
                  </motion.h1>
                  <motion.p
                    className="text-zinc-500 dark:text-zinc-400 text-lg"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    What currency does your business use?
                  </motion.p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-lg mx-auto max-h-[400px] overflow-y-auto pr-1">
                  {CURRENCY_OPTIONS.map((opt, i) => {
                    const isSelected = currency === opt.code;
                    return (
                      <motion.button
                        key={opt.code}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 + i * 0.02 }}
                        onClick={() => setCurrency(opt.code)}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left',
                          isSelected
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
                            : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-600'
                        )}
                      >
                        <DollarSign className={cn('w-5 h-5 shrink-0', isSelected ? 'text-orange-600' : 'text-zinc-400')} />
                        <div className="min-w-0">
                          <p className={cn('font-medium text-sm', isSelected ? 'text-orange-700 dark:text-orange-300' : 'text-zinc-700 dark:text-zinc-300')}>
                            {opt.code}
                          </p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{opt.name}</p>
                        </div>
                        {isSelected && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 1: Business Type */}
            {step === STEP_BUSINESS && (
              <motion.div key="business" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                    What type of business are you running?
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400">
                    This customizes your POS experience and terminology
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {BUSINESS_OPTIONS.map((option, i) => {
                    const Icon = option.icon;
                    const isSelected = businessType === option.value;
                    return (
                      <motion.button
                        key={option.value}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.05 }}
                        onClick={() => setBusinessType(option.value)}
                        className={cn(
                          'relative flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all text-center',
                          isSelected
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30 shadow-md'
                            : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-600'
                        )}
                      >
                        {isSelected && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-2 right-2 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                        <Icon className={cn('w-8 h-8', isSelected ? 'text-orange-600 dark:text-orange-400' : 'text-zinc-400 dark:text-zinc-500')} />
                        <span className={cn('font-medium text-sm', isSelected ? 'text-orange-700 dark:text-orange-300' : 'text-zinc-700 dark:text-zinc-300')}>{option.label}</span>
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 leading-tight">{option.description}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 2: Create Cashiers */}
            {step === STEP_CASHIERS && (
              <motion.div key="cashiers" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center mx-auto mb-4">
                    <UserPlus className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                    Set up cashier accounts
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400">
                    Cashiers use a 4-digit PIN to sign in at the register
                  </p>
                </div>

                {/* Virtual Cashier explanation */}
                <div className="mb-5 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800 dark:text-blue-200">Your "Virtual Cashier" account</p>
                      <p className="text-blue-700 dark:text-blue-300 mt-1">
                        As the owner, you already have a Virtual Cashier account tied to your login.
                        You can process sales yourself without creating a separate cashier.
                        Add cashier accounts below for anyone else who will be using the register.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 max-h-[260px] overflow-y-auto pr-1">
                  {cashiers.map((cashier, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex gap-2 items-start"
                    >
                      <input
                        type="text"
                        placeholder="First name"
                        value={cashier.firstName}
                        onChange={(e) => updateCashier(idx, 'firstName', e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Last name"
                        value={cashier.lastName}
                        onChange={(e) => updateCashier(idx, 'lastName', e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="PIN"
                        maxLength={4}
                        value={cashier.pin}
                        onChange={(e) => updateCashier(idx, 'pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
                        className="w-20 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 font-mono text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {cashiers.length > 1 && (
                        <button onClick={() => removeCashier(idx)} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>

                <button
                  onClick={addCashier}
                  className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add another cashier
                </button>
              </motion.div>
            )}

            {/* STEP 3: Add Products */}
            {step === STEP_PRODUCTS && (
              <motion.div key="products" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-green-100 dark:bg-green-950/30 flex items-center justify-center mx-auto mb-4">
                    <Package className="w-7 h-7 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                    Add your first {catalogLabel(businessType)}
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400">
                    Add a few now or import in bulk later. You can skip this step.
                  </p>
                </div>

                {/* CSV template download */}
                <div className="mb-4 flex items-center justify-center gap-3">
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Download CSV template
                  </button>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-y-auto pr-1">
                  {products.map((product, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex gap-2 items-start"
                    >
                      <input
                        type="text"
                        placeholder="Item name"
                        value={product.name}
                        onChange={(e) => updateProduct(idx, 'name', e.target.value)}
                        className="flex-[2] px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <input
                        type="text"
                        placeholder="Price"
                        value={product.price}
                        onChange={(e) => updateProduct(idx, 'price', e.target.value)}
                        className="w-24 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <input
                        type="text"
                        placeholder="Category"
                        value={product.category}
                        onChange={(e) => updateProduct(idx, 'category', e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      {products.length > 1 && (
                        <button onClick={() => removeProduct(idx)} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>

                <button
                  onClick={addProduct}
                  className="mt-3 flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add another item
                </button>
              </motion.div>
            )}

            {/* STEP 4: Optional Staff */}
            {step === STEP_STAFF && (
              <motion.div key="staff" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-violet-100 dark:bg-violet-950/30 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-violet-600 dark:text-violet-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                    Invite team members
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400">
                    Optional -- invite managers or admins. You can always do this later in Settings.
                  </p>
                </div>

                <div className="space-y-3 max-w-md mx-auto">
                  {staffEmails.map((email, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex gap-2"
                    >
                      <input
                        type="email"
                        placeholder="team@example.com"
                        value={email}
                        onChange={(e) => {
                          const updated = [...staffEmails];
                          updated[idx] = e.target.value;
                          setStaffEmails(updated);
                        }}
                        className="flex-1 px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                      {staffEmails.length > 1 && (
                        <button onClick={() => removeStaffEmail(idx)} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>

                <div className="flex justify-center mt-3">
                  <button
                    onClick={addStaffEmail}
                    className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add another
                  </button>
                </div>
              </motion.div>
            )}

            {/* STEP 5: Logo Upload */}
            {step === STEP_LOGO && (
              <motion.div key="logo" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-pink-100 dark:bg-pink-950/30 flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="w-7 h-7 text-pink-600 dark:text-pink-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                    Upload your store logo
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400">
                    Optional -- this appears on receipts and your dashboard
                  </p>
                </div>

                <div className="flex flex-col items-center gap-4 max-w-sm mx-auto">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleLogoSelect}
                    className="hidden"
                  />

                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-32 h-32 rounded-2xl object-contain bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 p-2"
                      />
                      <button
                        onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      className="w-32 h-32 rounded-2xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 flex flex-col items-center justify-center gap-2 text-zinc-400 hover:border-pink-400 hover:text-pink-500 transition-colors"
                    >
                      <Upload className="w-8 h-8" />
                      <span className="text-xs font-medium">Upload</span>
                    </button>
                  )}

                  <p className="text-xs text-zinc-400">PNG, JPG, SVG, or WebP. Max 2MB.</p>

                  {!logoPreview && (
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      className="px-4 py-2 bg-pink-100 dark:bg-pink-950/30 text-pink-700 dark:text-pink-300 rounded-lg text-sm font-medium hover:bg-pink-200 dark:hover:bg-pink-950/50 transition-colors"
                    >
                      Choose file
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* STEP 6: Theme Color */}
            {step === STEP_THEME && (
              <motion.div key="theme" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-violet-100 dark:bg-violet-950/30 flex items-center justify-center mx-auto mb-4">
                    <Palette className="w-7 h-7 text-violet-600 dark:text-violet-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                    Pick your theme color
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400">
                    This sets the accent color across your entire POS
                  </p>
                </div>

                <div className="grid grid-cols-4 gap-3 max-w-sm mx-auto">
                  {THEME_OPTIONS.map((option, i) => {
                    const isSelected = accentColor === option.value;
                    return (
                      <motion.button
                        key={option.value}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.05 + i * 0.04 }}
                        onClick={() => setSelectedAccent(option.value)}
                        className={cn(
                          'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all',
                          isSelected
                            ? 'border-zinc-900 dark:border-white shadow-md'
                            : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'
                        )}
                      >
                        <div
                          className={cn('w-10 h-10 rounded-full shadow-inner', option.color)}
                          style={isSelected ? { boxShadow: `0 0 0 3px white, 0 0 0 5px ${option.value}` } : undefined}
                        />
                        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">{option.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 7: Finish / Store URL */}
            {step === STEP_FINISH && (
              <motion.div key="finish" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-100 dark:bg-indigo-950/30 flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                    You're all set!
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400">
                    Bookmark your store URL -- you and your team will use it to sign in
                  </p>
                </div>

                <div className="max-w-md mx-auto">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="bg-zinc-100 dark:bg-zinc-800 rounded-xl p-5 mb-4"
                  >
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2 font-medium uppercase tracking-wider">
                      Your store address
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="text-lg font-mono font-semibold text-zinc-900 dark:text-white flex-1 break-all">
                        {organization?.slug || 'your-store'}.blazepos.app
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`${organization?.slug || 'your-store'}.blazepos.app`);
                          toast.success('Copied to clipboard');
                        }}
                        className="shrink-0"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4"
                  >
                    <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-1">
                      Save this URL
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Share it with your team members so they can log in. You can also find it later in Settings.
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>

          {/* Navigation buttons */}
          <motion.div
            className="flex items-center justify-between mt-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={goBack}
              disabled={step === 0}
              className={cn(step === 0 && 'invisible')}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <div className="flex items-center gap-1.5">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-300',
                    i === step ? 'w-6 bg-orange-500' : 'w-1.5 bg-zinc-300 dark:bg-zinc-600'
                  )}
                />
              ))}
            </div>

            {step < TOTAL_STEPS - 1 ? (
              <div className="flex items-center gap-2">
                {(step === STEP_CASHIERS || step === STEP_PRODUCTS || step === STEP_STAFF || step === STEP_LOGO) && (
                  <Button variant="ghost" onClick={goNext} className="text-zinc-500">
                    Skip
                  </Button>
                )}
                <Button
                  onClick={goNext}
                  disabled={!canAdvance()}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleFinish}
                disabled={!canAdvance() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Get Started
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
